import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { DAILY_SALES_QUERY, SALES_PERIOD_SUMMARIES_QUERY, STORES_DETAIL_QUERY } from '../graphql/queries';
import { useCalendarEvents } from '../hooks/useCalendarEvents';
import { useSelectedStore } from '../hooks/useSelectedStore';
import { gql } from '../lib/api';
import {
  buildAnnualPanel, buildMonthlyPanel, priorYearLunchRatio, summaryMonth,
  type DailySalesRow, type MonthlySalesRow, type SalesPeriodPanel, type SalesPeriodPoint,
} from '../lib/salesPeriod';
import { segmentMonthlyTarget, STORE_MONTHLY_SALES_TARGETS } from '../lib/salesTarget';

type Segment = 'all' | 'lunch' | 'dinner';
type Mode = 'monthly' | 'annual' | 'yearly';

interface Store {
  id: string;
  name: string;
  openDate: string | null;
  lunchStart: string | null;
  lunchEnd: string | null;
  airregiBusinessDayStart: string | null;
}

interface DisplayOption { key: string; store: Store; segment: Segment; label: string }

const yen = (value: number): string => `¥${Math.round(value).toLocaleString('ja-JP')}`;
const todayJst = (): string => new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());
const yearMonths = (year: number): string[] =>
  Array.from({ length: 12 }, (_, index) => `${year}-${String(index + 1).padStart(2, '0')}`);
const modes: Record<Mode, { title: string; eyebrow: string; description: string; periodLabel: string }> = {
  monthly: {
    title: '月間売上', eyebrow: 'MONTHLY / DAILY',
    description: '1月から12月まで、各月を1グラフとして日別の売上・来客数・最低目標を比較します。',
    periodLabel: '表示年',
  },
  annual: {
    title: '年間売上', eyebrow: 'ANNUAL / MONTHLY',
    description: '10年分を縦に並べ、各年を1グラフとして1月から12月の推移を比較します。',
    periodLabel: '最終年',
  },
  yearly: {
    title: '年別売上', eyebrow: 'YEARLY / DECADE',
    description: '直近10年を1グラフにまとめ、年ごとの売上・来客数・最低目標を比較します。',
    periodLabel: '最終年',
  },
};

const displayOptionsFor = (stores: Store[]): DisplayOption[] => [...stores]
  .sort((a, b) => Number(Boolean(b.lunchStart)) - Number(Boolean(a.lunchStart)))
  .flatMap((store): DisplayOption[] => store.lunchStart && store.lunchEnd
    ? [
      { key: `${store.id}:lunch`, store, segment: 'lunch', label: `${store.name}（ランチ）` },
      { key: `${store.id}:dinner`, store, segment: 'dinner', label: `${store.name}（ディナー）` },
    ]
    : [{ key: `${store.id}:all`, store, segment: 'all', label: store.name }]);

const fetchSummaries = async (storeId: string, year: number): Promise<MonthlySalesRow[]> => {
  const result = await gql<{ monthlySummary: MonthlySalesRow[] }>(SALES_PERIOD_SUMMARIES_QUERY, {
    storeId, fromYearMonth: `${year}-01`, toYearMonth: `${year}-12`,
  });
  return result.monthlySummary;
};

const fetchDaily = async (storeId: string, yearMonth: string): Promise<DailySalesRow[]> => {
  const result = await gql<{ dailySales: DailySalesRow[] }>(DAILY_SALES_QUERY, { storeId, yearMonth });
  return result.dailySales;
};

const SalesChart = ({ points, salesMax, customersMax, dayAxis }: {
  points: SalesPeriodPoint[]; salesMax: number; customersMax: number; dayAxis?: boolean;
}) => (
  <div className="sales-period-chart" role="img" aria-label="売上、来客数、最低目標の推移">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={points} margin={{ top: 12, right: 6, bottom: 2, left: 0 }}>
        <CartesianGrid stroke="#e9e3dd" strokeDasharray="2 4" vertical={false} />
        <XAxis dataKey="label" interval={dayAxis ? 4 : 0} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#887f77' }} />
        <YAxis yAxisId="sales" width={54} domain={[0, salesMax]} tickLine={false} axisLine={false}
          tick={{ fontSize: 9, fill: '#887f77' }} tickFormatter={(value: number) => `${Math.round(value / 10000)}万`} />
        <YAxis yAxisId="customers" orientation="right" width={36} domain={[0, customersMax]}
          tickLine={false} axisLine={false} tick={{ fontSize: 9, fill: '#b4688b' }} />
        <Tooltip formatter={(value, name) => {
          if (value === null || value === undefined) return ['データなし', name];
          return [name === '来客数' ? `${Number(value).toLocaleString()}人` : yen(Number(value)), name];
        }} labelFormatter={(_, payload) => {
          const point = payload?.[0]?.payload as SalesPeriodPoint | undefined;
          return point?.key ?? '';
        }} />
        <Line yAxisId="sales" dataKey="target" name="最低目標" stroke="#bc8650" strokeDasharray="5 4"
          strokeWidth={1.5} dot={false} connectNulls={false} isAnimationActive={false} />
        <Line yAxisId="sales" dataKey="sales" name="売上" stroke="#00a8b8" strokeWidth={2.5}
          dot={{ r: 2.3, fill: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={false} />
        <Line yAxisId="customers" dataKey="customers" name="来客数" stroke="#d6006c" strokeWidth={2.2}
          dot={{ r: 2.1, fill: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const Panel = ({ panel, index, mode, salesMax, customersMax }: {
  panel: SalesPeriodPanel; index: number; mode: Mode; salesMax: number; customersMax: number;
}) => {
  const gap = panel.sales - panel.target;
  const status = panel.future ? '未到来' : !panel.hasData ? 'データなし' : panel.current ? '進行中' : gap >= 0 ? '達成' : '未達';
  return (
    <article className={`sales-period-panel${panel.current ? ' sales-period-panel-current' : ''}`}>
      <header className="sales-period-panel-head">
        <div>
          <span className="sales-period-panel-index">{String(index + 1).padStart(2, '0')} / {mode === 'monthly' ? '12' : '10'}</span>
          <h2>{panel.label}</h2>
        </div>
        <span className={`sales-period-status${status === '達成' ? ' sales-period-status-positive' : ''}`}>{status}</span>
      </header>
      <div className="sales-period-panel-metrics">
        <span><small>売上</small><strong>{panel.hasData ? yen(panel.sales) : '—'}</strong></span>
        <span><small>来客</small><strong>{panel.hasData ? `${panel.customers.toLocaleString()}人` : '—'}</strong></span>
        <span><small>最低目標</small><strong>{panel.future ? '—' : yen(panel.target)}</strong></span>
        <span><small>目標差</small><strong className={gap >= 0 ? 'sales-period-gap-positive' : 'sales-period-gap-negative'}>
          {panel.hasData && !panel.current ? `${gap >= 0 ? '+' : '−'}${yen(Math.abs(gap))}` : '—'}
        </strong></span>
      </div>
      {panel.excludedEvents > 0 && <p className="sales-period-event-note">イベント日 {panel.excludedEvents}日をKPIから除外</p>}
      <SalesChart points={panel.points} salesMax={salesMax} customersMax={customersMax} dayAxis={mode === 'monthly'} />
      {!panel.hasData && <p className="sales-period-empty">{panel.future ? '対象期間はまだ始まっていません。' : 'この期間の売上データはありません。'}</p>}
    </article>
  );
};

const SalesPeriodPage = ({ mode }: { mode: Mode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedStoreId, setStoredStoreId] = useSelectedStore();
  const today = todayJst();
  const yearParam = Number(searchParams.get('year'));
  const [year, setYear] = useState(yearParam >= 2000 && yearParam <= 2100 ? yearParam : Number(today.slice(0, 4)));
  const [selectedKey, setSelectedKey] = useState(`${searchParams.get('store') ?? storedStoreId}:${searchParams.get('segment') ?? 'all'}`);
  const config = modes[mode];
  const firstYear = mode === 'monthly' ? year : year - 9;
  const visibleYears = useMemo(() => Array.from({ length: mode === 'monthly' ? 1 : 10 }, (_, index) => firstYear + index), [firstYear, mode]);
  const summaryYears = useMemo(() => Array.from({ length: mode === 'monthly' ? 2 : 11 }, (_, index) => firstYear - 1 + index), [firstYear, mode]);

  const storesQ = useQuery({ queryKey: ['storesDetail'], queryFn: () => gql<{ stores: Store[] }>(STORES_DETAIL_QUERY) });
  const stores = useMemo(() => storesQ.data?.stores ?? [], [storesQ.data?.stores]);
  const options = useMemo(() => displayOptionsFor(stores), [stores]);
  const selectedStoreId = selectedKey.split(':')[0];
  const selectedOption = options.find((option) => option.key === selectedKey)
    ?? options.find((option) => option.store.id === selectedStoreId)
    ?? options[0];
  const storeId = selectedOption?.store.id ?? '';
  const segment = selectedOption?.segment ?? 'all';

  useEffect(() => {
    if (selectedOption) setStoredStoreId(storeId);
  }, [selectedOption, setStoredStoreId, storeId]);
  useEffect(() => {
    if (!storeId) return;
    const next = new URLSearchParams(searchParams);
    next.set('store', storeId);
    if (segment === 'all') next.delete('segment'); else next.set('segment', segment);
    next.set('year', String(year));
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [searchParams, segment, setSearchParams, storeId, year]);

  const eventsQ = useCalendarEvents(`${firstYear}-01-01`, `${year}-12-31`);
  const summariesQ = useQueries({ queries: summaryYears.map((queryYear) => ({
    queryKey: ['salesPeriodSummaries', storeId, queryYear],
    enabled: Boolean(storeId) && queryYear <= Number(today.slice(0, 4)),
    queryFn: () => fetchSummaries(storeId, queryYear),
    staleTime: 5 * 60_000,
  })) });
  const monthlyDates = useMemo(() => mode === 'monthly' ? yearMonths(year).filter((ym) => ym <= today.slice(0, 7)) : [], [mode, today, year]);
  const eventMonths = useMemo(() => mode === 'monthly' ? [] : [...new Set(
    (eventsQ.data?.calendarEvents ?? [])
      .filter((event) => event.storeId === storeId && event.date <= today && event.date >= `${firstYear}-01-01`)
      .map((event) => event.date.slice(0, 7)),
  )], [eventsQ.data, firstYear, mode, storeId, today]);
  const dailyMonths = mode === 'monthly' ? monthlyDates : eventMonths;
  const dailyQ = useQueries({ queries: dailyMonths.map((yearMonth) => ({
    queryKey: ['salesPeriodDaily', storeId, yearMonth],
    enabled: Boolean(storeId),
    queryFn: () => fetchDaily(storeId, yearMonth),
    staleTime: 5 * 60_000,
  })) });

  const summaries = useMemo(() => new Map(summariesQ.flatMap((query) => query.data ?? [])
    .map((row) => [summaryMonth(row), row])), [summariesQ]);
  const dayRows = useMemo(() => new Map(dailyMonths.map((yearMonth, index) => [yearMonth, dailyQ[index]?.data ?? []])), [dailyMonths, dailyQ]);
  const events = useMemo(() => new Set((eventsQ.data?.calendarEvents ?? [])
    .filter((event) => event.storeId === storeId).map((event) => event.date)), [eventsQ.data, storeId]);
  const panels = useMemo(() => visibleYears.flatMap((visibleYear) => {
    const lunchRatio = priorYearLunchRatio(visibleYear, summaries).ratio;
    return mode === 'monthly'
      ? yearMonths(visibleYear).map((yearMonth) => buildMonthlyPanel({
        yearMonth, rows: dayRows.get(yearMonth), events, storeId, segment, lunchRatio, today,
        openDate: selectedOption?.store.openDate,
      }))
      : [buildAnnualPanel({
        year: visibleYear, summaries, eventDays: dayRows, events, storeId, segment, lunchRatio, today,
        openDate: selectedOption?.store.openDate,
      })];
  }), [dayRows, events, mode, segment, selectedOption, storeId, summaries, today, visibleYears]);
  const yearlyPoints = useMemo(() => panels.map((panel): SalesPeriodPoint => ({
    key: panel.key, label: panel.key, sales: panel.hasData ? panel.sales : null,
    customers: panel.hasData ? panel.customers : null, target: panel.future ? null : panel.target,
    excluded: panel.excludedEvents > 0,
  })), [panels]);
  const chartPoints = mode === 'yearly' ? yearlyPoints : panels.flatMap((panel) => panel.points);
  const salesMax = Math.max(10_000, Math.ceil(Math.max(...chartPoints.map((point) => Math.max(point.sales ?? 0, point.target ?? 0)), 0) * 1.15 / 10_000) * 10_000);
  const customersMax = Math.max(10, Math.ceil(Math.max(...chartPoints.map((point) => point.customers ?? 0), 0) * 1.15 / 10) * 10);
  const referencePanel = panels.find((panel) => panel.current) ?? [...panels].reverse().find((panel) => panel.hasData);
  const referenceYear = Number(referencePanel?.key.slice(0, 4) ?? year);
  const ratio = priorYearLunchRatio(referenceYear, summaries);
  const loading = storesQ.isLoading || eventsQ.isLoading || summariesQ.some((query) => query.isLoading) || dailyQ.some((query) => query.isLoading);
  const error = storesQ.isError || eventsQ.isError || summariesQ.some((query) => query.isError) || dailyQ.some((query) => query.isError);

  return <div className="sales-period-page">
    <header className="sales-period-heading">
      <div><span className="kpi-weekly-kicker">KPI LAB / {config.eyebrow}</span><h1>{config.title}</h1><p>{config.description}</p></div>
      <label className="kpi-weekly-store"><span>表示店舗・時間帯</span>
        <select value={selectedOption?.key ?? ''} onChange={(event) => setSelectedKey(event.target.value)}>
          {options.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
        </select>
      </label>
    </header>
    <div className="kpi-weekly-note"><span>KPI検討用</span><p>Airレジの来店日と店舗マスタの営業日・ランチ／ディナー集計終点で集計します。イベント日を除外し、未取込期間はデータなしと表示します。進行中の期間は暫定値です。</p></div>
    <div className="sales-period-controls">
      <label><span>{config.periodLabel}</span><input type="number" min="2000" max="2100" value={year}
        onChange={(event) => { const next = Number(event.target.value); if (next >= 2000 && next <= 2100) setYear(next); }} /></label>
      <div className="kpi-weekly-range-actions">
        <button type="button" onClick={() => setYear((current) => Math.max(2000, current - 1))}>← 前年</button>
        <button type="button" onClick={() => setYear((current) => Math.min(2100, current + 1))}>翌年 →</button>
        <button type="button" onClick={() => setYear(Number(today.slice(0, 4)))}>最新年</button>
      </div>
      <p>{mode === 'monthly' ? `${year}年1月〜12月` : `${firstYear}年〜${year}年`}</p>
    </div>
    <div className={`kpi-sales-target-strip${segment === 'all' ? ' kpi-sales-target-strip-two' : ''}`}>
      <div><span>表示店舗の月間最低目標</span><strong>{yen(STORE_MONTHLY_SALES_TARGETS[storeId] ?? 0)}</strong><small>{selectedOption?.store.name}</small></div>
      <div><span>表示中の時間帯・月間目標</span><strong>{yen(segmentMonthlyTarget(storeId, segment, ratio.ratio))}</strong><small>{selectedOption?.label}</small></div>
      {segment !== 'all' && <div><span>LOOP 売上配分</span><strong>ランチ {(ratio.ratio * 100).toFixed(1)}% ／ ディナー {((1 - ratio.ratio) * 100).toFixed(1)}%</strong>
        <small>{ratio.months ? `${referenceYear - 1}年の実績（${ratio.months}か月）` : '前年の実績がないため50%ずつで仮配分'}</small></div>}
    </div>
    {error && <section className="kpi-weekly-state" role="alert">売上データを取得できませんでした。再読み込みしてお試しください。</section>}
    {!error && loading && <section className="kpi-weekly-state">売上データを読み込んでいます…</section>}
    {!error && !loading && <>
      <div className="sales-period-legend" aria-label="グラフの凡例"><span><i className="sales-period-legend-sales" />売上</span><span><i className="sales-period-legend-customers" />来客数</span><span><i className="sales-period-legend-target" />最低目標</span></div>
      {mode === 'yearly' ? <section className="sales-period-yearly">
        <div className="sales-period-yearly-heading"><span>10-YEAR VIEW</span><h2>{firstYear}年 — {year}年</h2><p>{selectedOption?.label}</p></div>
        <SalesChart points={yearlyPoints} salesMax={salesMax} customersMax={customersMax} />
        <div className="sales-period-yearly-stats">{panels.map((panel) => <div key={panel.key}><strong>{panel.label}</strong><span>{panel.hasData ? yen(panel.sales) : 'データなし'}</span><small>{panel.hasData ? `${panel.customers.toLocaleString()}人` : '—'}</small></div>)}</div>
      </section> : <section className="sales-period-panels" aria-label={`${config.title}のグラフ`}>
        {panels.map((panel, index) => <Panel key={panel.key} panel={panel} index={index} mode={mode} salesMax={salesMax} customersMax={customersMax} />)}
      </section>}
    </>}
  </div>;
};

export const MonthlySalesPage = () => <SalesPeriodPage mode="monthly" />;
export const AnnualSalesPage = () => <SalesPeriodPage mode="annual" />;
export const YearlySalesPage = () => <SalesPeriodPage mode="yearly" />;
